/**
 * API client for guided-tour progress (EPIC #1321 part 7/9, REQ D4-ONB-009).
 *
 * Wraps the self-scoped TourProgress endpoints (part 2/9, #1323):
 *   GET  /api/v1/auth/tours/            -> the caller's own tour states
 *   POST /api/v1/auth/tours/upsert/     -> start/advance/dismiss/complete/reset
 *
 * Rows are strictly self-scoped server-side, so the client never sends a user
 * id. The Guided Tours launcher (Training Vault) reads these to show
 * Not started / In progress (Resume) / Completed (Retake) per tour and to drive
 * reset. The model carries NO personal data -- only a tour slug + status + step.
 */
import { apiRequest } from "./client";

/** Server-side per-(user, tour) status. Mirrors backend TourProgress.Status. */
export type TourStatus =
  | "not_started"
  | "in_progress"
  | "dismissed"
  | "completed";

/** One row of the caller's own tour progress. */
export interface TourProgress {
  id: number;
  tour_id: string;
  status: TourStatus;
  /** Zero-based index of the last step the user reached. */
  current_step: number;
  created_at: string;
  updated_at: string;
}

/** Fetch every tour-progress row belonging to the calling user. */
export async function fetchTourProgress(): Promise<TourProgress[]> {
  // The viewset disables pagination, so this returns a bare array.
  return apiRequest<TourProgress[]>("/auth/tours/");
}

export interface TourProgressUpsert {
  tour_id: string;
  status?: TourStatus;
  current_step?: number;
}

/** Create or update the caller's progress for one tour (idempotent upsert). */
export async function upsertTourProgress(
  payload: TourProgressUpsert,
): Promise<TourProgress> {
  return apiRequest<TourProgress>("/auth/tours/upsert/", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/**
 * Reset a tour back to "not started" so it can be retaken from the beginning.
 * Idempotent: keyed on (user, tour_id) server-side.
 */
export async function resetTourProgress(
  tourId: string,
): Promise<TourProgress> {
  return upsertTourProgress({
    tour_id: tourId,
    status: "not_started",
    current_step: 0,
  });
}

/**
 * Clear the first-login welcome prompt for the caller (D4-ONB-006, #1324).
 *
 * Answering EITHER WAY -- taking a tour or dismissing the prompt -- clears
 * eligibility server-side so it never re-appears on another device/session.
 * Idempotent: clearing an already-cleared flag is a no-op 200.
 */
export async function answerWelcomePrompt(): Promise<{
  welcome_prompt_eligible: boolean;
}> {
  return apiRequest<{ welcome_prompt_eligible: boolean }>(
    "/auth/welcome-prompt/answer/",
    { method: "POST", body: JSON.stringify({}) },
  );
}
